import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Search, AlertTriangle, CheckCircle2, Pencil, Save, X, History, RotateCcw, GitPullRequest } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["All", "Process", "Policy", "Decision Rule", "People/Role", "FAQ"];
const EDITABLE_CATS = ["Process", "Policy", "Decision Rule", "People/Role", "FAQ"];

export default function Knowledge() {
  const { canWrite } = useAuth();
  const [params, setParams] = useSearchParams();
  const initialCat = params.get("category") || "All";
  const [q, setQ] = useState("");
  const [category, setCategory] = useState(initialCat);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const [revisions, setRevisions] = useState([]);
  const [showRevisions, setShowRevisions] = useState(false);
  const [showSuggest, setShowSuggest] = useState(false);
  const [suggest, setSuggest] = useState({ summary: "", content: "", reason: "" });
  const [submittingSuggest, setSubmittingSuggest] = useState(false);

  const loadRevisions = async (kid) => {
    try {
      const { data } = await api.get(`/knowledge/${kid}/revisions`);
      setRevisions(data);
    } catch { setRevisions([]); }
  };

  const restoreRevision = async (revId) => {
    if (!selected) return;
    if (!window.confirm("Restore this revision? Current content will be snapshotted first.")) return;
    try {
      const { data } = await api.post(`/knowledge/${selected.id}/revisions/${revId}/restore`);
      setSelected(data);
      toast.success("Restored");
      await loadRevisions(selected.id);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to restore");
    }
  };

  const openSuggest = () => {
    setSuggest({
      summary: selected?.summary || "",
      content: selected?.content || "",
      reason: "",
    });
    setShowSuggest(true);
  };

  const submitSuggestion = async () => {
    if (!selected || submittingSuggest) return;
    const changes = {};
    if (suggest.summary !== (selected.summary || "")) changes.summary = suggest.summary;
    if (suggest.content !== (selected.content || "")) changes.content = suggest.content;
    if (Object.keys(changes).length === 0) {
      toast.error("Change at least one field to submit a suggestion");
      return;
    }
    setSubmittingSuggest(true);
    try {
      await api.post(`/knowledge/${selected.id}/suggestions`, {
        changes,
        reason: suggest.reason || undefined,
      });
      toast.success("Suggestion submitted for review");
      setShowSuggest(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit suggestion");
    } finally { setSubmittingSuggest(false); }
  };

  const load = async () => {
    const p = {};
    if (category !== "All") p.category = category;
    if (q) p.q = q;
    const { data } = await api.get("/knowledge", { params: p });
    setItems(data);
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, [category]);

  // Deep-link support — /app/knowledge?open=<kid> auto-opens the entry.
  useEffect(() => {
    const kid = params.get("open");
    if (!kid) return;
    (async () => {
      try {
        const { data } = await api.get(`/knowledge/${kid}`);
        setSelected(data);
        // Clear the param after handling so a refresh doesn't re-trigger.
        const next = new URLSearchParams(params);
        next.delete("open");
        setParams(next, { replace: true });
      } catch { /* silent */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applySearch = (e) => { e.preventDefault(); load(); };

  const flag = async (id) => {
    await api.post(`/knowledge/${id}/flag-outdated`);
    toast("Flagged as outdated");
    load();
  };
  const approve = async (id) => {
    await api.post(`/knowledge/${id}/approve`);
    toast.success("Approved");
    load();
  };

  const startEdit = () => {
    setEdit({
      title: selected.title,
      summary: selected.summary || "",
      content: selected.content || "",
      category: selected.category,
      department: selected.department,
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/knowledge/${selected.id}`, edit);
      toast.success("Saved");
      setSelected(data);
      setEditing(false);
      setEdit(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const closeDialog = () => {
    setSelected(null);
    setEditing(false);
    setShowRevisions(false);
    setRevisions([]);
    setEdit(null);
  };

  return (
    <div className="p-8 max-w-6xl">
      <header className="mb-8">
        <p className="eyebrow mb-3">Knowledge</p>
        <h1 className="font-display text-3xl">Structured knowledge</h1>
        <p className="mt-2 text-muted-foreground">Every atomic fact, categorized, editable, and semantically searchable.</p>
      </header>

      <form onSubmit={applySearch} className="flex gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search titles, summaries, content…" className="pl-9" data-testid="knowledge-search-input" />
        </div>
        <Button type="submit" data-testid="knowledge-search-btn">Search</Button>
      </form>

      <div className="flex flex-wrap gap-2 mb-6">
        {CATEGORIES.map((c) => (
          <button
            key={c}
            onClick={() => { setCategory(c); setParams(c === "All" ? {} : { category: c }); }}
            className={`px-3 py-1.5 text-xs uppercase tracking-[0.15em] rounded-full border transition-colors ${
              category === c ? "bg-foreground text-background border-foreground" : "border-border/60 hover:border-foreground/40 text-muted-foreground"
            }`}
            data-testid={`knowledge-filter-${c}`}
          >
            {c}
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <div className="card-flat p-10 text-center text-muted-foreground text-sm">
          No knowledge entries yet. Ingest a source to populate this view.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((k) => (
            <button
              key={k.id}
              onClick={() => setSelected(k)}
              className="card-flat p-5 text-left hover-lift"
              data-testid={`knowledge-card-${k.id}`}
            >
              <div className="flex items-center gap-2 mb-3">
                <Badge variant="secondary" className="text-[10px] uppercase tracking-wider">{k.category}</Badge>
                {k.status === "outdated" && (
                  <span className="text-[10px] uppercase text-amber-500 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> Outdated
                  </span>
                )}
              </div>
              <h3 className="font-display text-lg leading-snug mb-2 line-clamp-2">{k.title}</h3>
              <p className="text-sm text-muted-foreground line-clamp-3 leading-relaxed">{k.summary}</p>
              <div className="mt-4 text-xs text-muted-foreground font-mono">{k.department}</div>
            </button>
          ))}
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {selected && !editing && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="secondary" className="text-[10px] uppercase">{selected.category}</Badge>
                  <Badge variant="outline" className="text-[10px]">{selected.department}</Badge>
                  {selected.status === "outdated" && (
                    <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/30 text-[10px]">Outdated</Badge>
                  )}
                </div>
                <DialogTitle className="font-display text-2xl">{selected.title}</DialogTitle>
                <DialogDescription className="sr-only">Knowledge entry details</DialogDescription>
              </DialogHeader>
              <div className="mt-4 space-y-4">
                <p className="text-sm text-muted-foreground">{selected.summary}</p>
                <div className="whitespace-pre-wrap text-sm leading-relaxed">{selected.content}</div>
                {selected.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {selected.tags.map((t) => (
                      <span key={t} className="text-[10px] font-mono text-muted-foreground border border-border/60 rounded px-2 py-0.5">#{t}</span>
                    ))}
                  </div>
                )}
                <div className="flex flex-wrap gap-2 pt-4 border-t border-border/60">
                  <Button
                    variant="ghost"
                    onClick={() => {
                      const next = !showRevisions;
                      setShowRevisions(next);
                      if (next && revisions.length === 0) loadRevisions(selected.id);
                    }}
                    className="rounded-full"
                    data-testid="knowledge-history-btn"
                  >
                    <History className="w-4 h-4 mr-2" /> History
                  </Button>
                  {canWrite ? (
                    <>
                      <Button onClick={startEdit} variant="outline" className="rounded-full" data-testid="knowledge-edit-btn">
                        <Pencil className="w-4 h-4 mr-2" /> Edit
                      </Button>
                      {selected.status === "outdated" ? (
                        <Button onClick={() => { approve(selected.id); closeDialog(); }} className="rounded-full" data-testid="knowledge-approve-btn">
                          <CheckCircle2 className="w-4 h-4 mr-2" /> Mark current
                        </Button>
                      ) : (
                        <Button variant="outline" onClick={() => { flag(selected.id); closeDialog(); }} className="rounded-full" data-testid="knowledge-flag-btn">
                          <AlertTriangle className="w-4 h-4 mr-2" /> Flag outdated
                        </Button>
                      )}
                    </>
                  ) : (
                    <p className="text-[11px] text-muted-foreground" data-testid="knowledge-viewer-hint">Viewer role — suggest changes below.</p>
                  )}
                  <Button
                    variant="outline"
                    onClick={openSuggest}
                    className="rounded-full ml-auto"
                    data-testid="knowledge-suggest-btn"
                  >
                    <GitPullRequest className="w-4 h-4 mr-2" /> Suggest edit
                  </Button>
                </div>
                {showRevisions && (
                  <div className="pt-4 border-t border-border/60" data-testid="knowledge-revisions-panel">
                    <p className="eyebrow mb-3">Revision history ({revisions.length})</p>
                    {revisions.length === 0 ? (
                      <p className="text-xs text-muted-foreground">No previous revisions yet. Every edit will snapshot the prior version here.</p>
                    ) : (
                      <ul className="space-y-2 max-h-64 overflow-auto pr-1">
                        {revisions.map((r) => (
                          <li key={r.id} className="text-xs border border-border/60 rounded-md p-3 flex items-start gap-3" data-testid={`knowledge-revision-${r.id}`}>
                            <div className="flex-1 min-w-0">
                              <p className="font-mono text-[10px] text-muted-foreground">
                                {new Date(r.created_at).toLocaleString()} · by {r.changed_by_name || r.changed_by?.slice(0, 8)}
                              </p>
                              <p className="mt-1 line-clamp-2 text-foreground/90">{r.snapshot?.summary || r.snapshot?.title || "(no summary)"}</p>
                            </div>
                            {canWrite && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => restoreRevision(r.id)}
                                className="rounded-full shrink-0"
                                data-testid={`knowledge-restore-${r.id}`}
                              >
                                <RotateCcw className="w-3.5 h-3.5 mr-1" /> Restore
                              </Button>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
          {selected && editing && edit && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-xl">Edit entry</DialogTitle>
                <DialogDescription className="sr-only">Modify title, category, department, summary, or content</DialogDescription>
              </DialogHeader>
              <div className="mt-4 space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="e-title">Title</Label>
                  <Input id="e-title" value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} data-testid="edit-title-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="e-cat">Category</Label>
                    <select
                      id="e-cat"
                      value={edit.category}
                      onChange={(e) => setEdit({ ...edit, category: e.target.value })}
                      className="w-full bg-background border border-input rounded-md h-9 px-3 text-sm"
                      data-testid="edit-category-select"
                    >
                      {EDITABLE_CATS.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="e-dep">Department</Label>
                    <Input id="e-dep" value={edit.department} onChange={(e) => setEdit({ ...edit, department: e.target.value })} data-testid="edit-department-input" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="e-sum">Summary</Label>
                  <Textarea id="e-sum" rows={2} value={edit.summary} onChange={(e) => setEdit({ ...edit, summary: e.target.value })} data-testid="edit-summary-input" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="e-content">Content</Label>
                  <Textarea id="e-content" rows={10} value={edit.content} onChange={(e) => setEdit({ ...edit, content: e.target.value })} data-testid="edit-content-input" />
                </div>
                <div className="flex gap-2 pt-4 border-t border-border/60">
                  <Button onClick={saveEdit} disabled={saving} className="rounded-full" data-testid="edit-save-btn">
                    <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save changes"}
                  </Button>
                  <Button variant="ghost" onClick={() => { setEditing(false); setEdit(null); }} className="rounded-full" data-testid="edit-cancel-btn">
                    <X className="w-4 h-4 mr-2" /> Cancel
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showSuggest} onOpenChange={setShowSuggest}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <GitPullRequest className="w-4 h-4" /> Suggest edit
            </DialogTitle>
            <DialogDescription>
              Propose changes for review. An Admin will approve or reject before your edits go live.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="sug-summary">Summary</Label>
              <Textarea
                id="sug-summary"
                rows={3}
                value={suggest.summary}
                onChange={(e) => setSuggest({ ...suggest, summary: e.target.value })}
                data-testid="suggest-summary-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="sug-content">Content</Label>
              <Textarea
                id="sug-content"
                rows={6}
                value={suggest.content}
                onChange={(e) => setSuggest({ ...suggest, content: e.target.value })}
                data-testid="suggest-content-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="sug-reason">Why? (optional)</Label>
              <Textarea
                id="sug-reason"
                rows={2}
                placeholder="e.g. this SOP is stale after the Q1 policy update"
                value={suggest.reason}
                onChange={(e) => setSuggest({ ...suggest, reason: e.target.value })}
                data-testid="suggest-reason-input"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" onClick={() => setShowSuggest(false)} className="rounded-full">Cancel</Button>
              <Button
                onClick={submitSuggestion}
                disabled={submittingSuggest}
                className="rounded-full"
                data-testid="suggest-submit-btn"
              >
                {submittingSuggest ? "Submitting…" : "Submit for review"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
